import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "./theme";
import {
  Clock, Wifi, Building2, Stethoscope, ClipboardList, Utensils, Sparkles,
  Heart, Zap, MessageCircle, Lightbulb, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, X, User, Timer,
} from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";

const CAT_ICONS = {
  "Tempo de espera":           Clock,
  "Comunicação da equipe":     Wifi,
  "Conforto/estrutura":        Building2,
  "Atendimento médico":        Stethoscope,
  "Processos administrativos": ClipboardList,
  "Alimentação":               Utensils,
  "Limpeza":                   Sparkles,
  "Equipe médica":             Stethoscope,
  "Enfermagem":                Heart,
  "Acolhimento":               Heart,
  "Estrutura":                 Building2,
  "Rapidez":                   Zap,
  "Comunicação":               MessageCircle,
};

function getIcon(categoria) {
  return CAT_ICONS[categoria] || Lightbulb;
}

const IMPACTO_COLOR = {
  "Alto":  T.red,
  "Médio": T.amber,
  "Baixo": T.green,
};

function RecModal({ exemplos, onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
      <Card style={{ width:"min(580px,95vw)", maxHeight:"80vh", overflowY:"auto", boxShadow:T.shadowLg }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <h3 style={{ margin:0, fontFamily:T.fontTitle, fontSize:16, color:T.textHi }}>Depoimentos relacionados</h3>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:T.textLow, cursor:"pointer", padding:4 }}>
            <X size={18} />
          </button>
        </div>
        {(exemplos||[]).length === 0 ? (
          <p style={{ color:T.textLow, fontSize:13, fontFamily:T.fontBody }}>Nenhum depoimento disponível.</p>
        ) : (
          exemplos.map((ex, i) => (
            <div key={i} style={{
              borderLeft:`2px solid ${T.accent}40`, paddingLeft:12,
              fontSize:13, color:T.textMid, fontStyle:"italic", lineHeight:1.6,
              marginBottom:12, fontFamily:T.fontBody,
            }}>
              "{ex}"
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

function TipCard({ tip, onPDSA }) {
  const [expanded, setExpanded]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const Icon = getIcon(tip.categoria);
  const isMelhoria = tip.tipo === "melhoria";
  const accentColor = isMelhoria ? T.amber : T.green;

  return (
    <>
      <Card
        noPadding
        style={{
          borderColor: expanded ? accentColor + "40" : T.border,
          marginBottom: 12,
          transition: T.transitionFast,
        }}
      >
        {/* Card header — clickable */}
        <div
          style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
          onClick={() => setExpanded(e => !e)}
        >
          <div style={{
            width: T.iconBoxSize, height: T.iconBoxSize, borderRadius: T.iconBoxRadius,
            background: `${accentColor}15`, border: `1px solid ${accentColor}30`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accentColor, flexShrink: 0,
          }}>
            <Icon size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: T.textHi, marginBottom: 2, fontFamily: T.fontTitle }}>
              {tip.categoria}
            </div>
            <div style={{ fontSize: 12, color: T.textLow, fontFamily: T.fontBody }}>
              {isMelhoria
                ? `${tip.count} reclamações · ${tip.pct}% dos feedbacks negativos`
                : `${tip.count} elogios · ${tip.pct}% dos feedbacks positivos`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            <Badge color={accentColor}>
              {isMelhoria ? "Melhoria" : "Manutenção"}
            </Badge>
            {(tip.exemplos||[]).length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={e => { e.stopPropagation(); setShowModal(true); }}
                style={{
                  background: T.bgInput, border: `1px solid ${T.border}`,
                  color: T.textLow, fontSize: 11,
                }}
              >
                Ver depoimentos
              </Button>
            )}
            <div style={{ color: T.textLow }}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
        </div>

        {/* Expanded recommendations */}
        {expanded && (
          <div style={{ borderTop: `1px solid ${T.border}`, padding: "16px 22px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLow, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.fontBody }}>
              Recomendações
            </div>
            {(tip.recomendacoes || []).map((rec, i) => (
              <div key={i} style={{
                background: T.bgBase, borderRadius: T.radiusSm, padding: "14px 16px",
                marginBottom: 10, border: `1px solid ${T.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: T.textHi, lineHeight: 1.4, flex: 1, fontFamily: T.fontTitle }}>
                    {rec.acao}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPDSA(tip, rec)}
                    style={{
                      background: T.accentDim, border: `1px solid ${T.accent}40`,
                      color: T.accent, flexShrink: 0,
                    }}
                  >
                    Criar PDSA
                  </Button>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Badge color={IMPACTO_COLOR[rec.impacto] || T.amber}>
                    Impacto {rec.impacto}
                  </Badge>
                  {rec.prazo && (
                    <Badge color={T.accent}>
                      <Timer size={10}/> {rec.prazo}
                    </Badge>
                  )}
                  {rec.responsavel && (
                    <Badge color={T.textMid}>
                      <User size={10}/> {rec.responsavel}
                    </Badge>
                  )}
                </div>
                {rec.indicador && (
                  <div style={{ marginTop: 8, fontSize: 11, color: T.textLow, fontFamily: T.fontBody }}>
                    <span style={{ color: T.green, fontWeight: 600 }}>Indicador: </span>{rec.indicador}
                  </div>
                )}
              </div>
            ))}
            {(tip.recomendacoes || []).length === 0 && (
              <div style={{ color: T.textLow, fontSize: 13, fontFamily: T.fontBody }}>Nenhuma recomendação gerada.</div>
            )}
          </div>
        )}
      </Card>

      {showModal && <RecModal exemplos={tip.exemplos || []} onClose={() => setShowModal(false)} />}
    </>
  );
}

const PERIODOS = [
  { key: "hoje",     label: "Hoje" },
  { key: "semana",   label: "Semana" },
  { key: "mes",      label: "Mês" },
  { key: "semestre", label: "Semestre" },
  { key: "ano",      label: "Ano" },
  { key: "todos",    label: "Todos" },
];

export default function Tips() {
  const [periodo, setPeriodo] = useState("mes");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const navigate              = useNavigate();

  const load = async (p) => {
    setLoading(true); setError(null);
    try {
      const qs = p ? `?periodo=${p}` : "";
      const r = await fetch(`/api/tips${qs}`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      setData(await r.json());
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(periodo); }, [periodo]);

  const handlePDSA = (tip, rec) => {
    const titulo = rec.acao;
    const descricao = `Área: ${tip.categoria} | Impacto: ${rec.impacto} | Prazo: ${rec.prazo} | Responsável: ${rec.responsavel} | Indicador: ${rec.indicador}`;
    navigate("/pdsa", { state: { cicloNovo: { titulo, descricao } } });
  };

  const melhorias    = (data?.tips || []).filter(t => t.tipo === "melhoria");
  const manutencoes  = (data?.tips || []).filter(t => t.tipo === "manutencao");

  return (
    <PageContainer style={{ color: T.textHi, fontFamily: T.fontBody }}>
      <PageHeader icon={Lightbulb} title="CXO Insights" description="Recomendações estratégicas baseadas em IA — foco em PDSA/PDCA hospitalar">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: T.bgInput, borderRadius: T.radiusSm, padding: 4, border: `1px solid ${T.border}` }}>
            {PERIODOS.map(p => (
              <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
                padding: "6px 12px", borderRadius: T.radiusXs, fontSize: 12, cursor: "pointer",
                background: periodo === p.key ? T.bgCard : "transparent",
                border: "none",
                color: periodo === p.key ? T.textHi : T.textLow,
                fontWeight: periodo === p.key ? 700 : 500,
                boxShadow: periodo === p.key ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                transition: T.transitionFast, fontFamily: T.fontBody, outline: "none",
              }}>{p.label}</button>
            ))}
          </div>
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => load(periodo)}>
            Atualizar
          </Button>
        </div>
      </PageHeader>

      {loading && (
        <PageLoading label="Analisando insights..." />
      )}

      {error && !loading && (
        <div style={{
          padding: 20, background: `${T.red}10`, border: `1px solid ${T.red}30`,
          borderRadius: T.radius, color: T.red, fontSize: 13, fontFamily: T.fontBody,
        }}>
          Erro ao carregar tips: {error}. Verifique a chave da API.
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Melhorias Prioritárias */}
          {melhorias.length > 0 && (
            <div style={{ marginBottom: 36 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <AlertTriangle size={16} color={T.amber} />
                <h2 style={{ margin: 0, fontSize: 16, fontFamily: T.fontTitle, fontWeight: 700, color: T.amber }}>
                  Melhorias Prioritárias
                </h2>
                <Badge color={T.amber}>{melhorias.length}</Badge>
              </div>
              {melhorias.map((tip, i) => (
                <TipCard key={i} tip={tip} onPDSA={handlePDSA} />
              ))}
            </div>
          )}

          {/* Manutenção de Excelência */}
          {manutencoes.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <CheckCircle size={16} color={T.green} />
                <h2 style={{ margin: 0, fontSize: 16, fontFamily: T.fontTitle, fontWeight: 700, color: T.green }}>
                  Manutenção de Excelência
                </h2>
                <Badge color={T.green}>{manutencoes.length}</Badge>
              </div>
              {manutencoes.map((tip, i) => (
                <TipCard key={i} tip={tip} onPDSA={handlePDSA} />
              ))}
            </div>
          )}

          {melhorias.length === 0 && manutencoes.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: T.textLow, fontSize: 14, fontFamily: T.fontBody }}>
              Nenhuma recomendação disponível. Sincronize os depoimentos primeiro.
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
